import type { IStatusRule } from "components/shared/StatusChip";
import StatusChip from "components/shared/StatusChip";
import { isDateWithinDays } from "helpers/utils";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ITerm } from "store/managementStore";
import type { TPeriod } from "store/periodStore";

import styles from "./style.module.scss";

type EndDateStatus = "expired" | "expires_soon";

interface ITermExpirationStatusWrapper {
    /**
     * Contents of the cell next to which the expiration status will be displayed
     */
    children: ReactNode;
    /**
     * Term data
     */
    term: ITerm;
    /**
     * Periods data
     */
    periods: TPeriod[];
}
/**
 * This component wrap a table cell to show expiration status chip
 */
export const TermExpirationStatusWrapper = ({ children, term, periods }: ITermExpirationStatusWrapper) => {
    const [t] = useTranslation();
    const { endDate, periodTypeId } = term;

    const termPeriodEndDate = periods.find((p) => p.periodTypeId == periodTypeId && p.isActive)?.periodEnd;

    const isRenewable = isTermRenewable(periods, term);

    let expiresStatus = "";

    if (isRenewable) expiresStatus = "expired";

    if (isDateWithinDays(endDate, 30) && isDateWithinDays(termPeriodEndDate, 30)) {
        expiresStatus = "expires_soon";
    }

    const endDateStatusRules: Record<EndDateStatus, IStatusRule> = {
        expired: { color: "red", text: t("Terms.Expired") },
        expires_soon: { color: "grey", text: t("Terms.ExpiresSoon") },
    };

    return (
        <div className={styles["expiration-status-wrapper"]}>
            {children}
            {!!expiresStatus && <StatusChip status={expiresStatus} statusRules={endDateStatusRules} hideDot />}
        </div>
    );
};

// Business dates are stored as UTC midnight: compare calendar days, not instants.
export const toUtcDay = (date: Date | string) => new Date(date).toISOString().slice(0, 10);

/** Backend orders the period type's periods by id and renews onto the first active one */
export const getRenewalPeriods = (
    periods: TPeriod[],
    periodTypeId: ITerm["periodTypeId"],
): { activePeriod?: TPeriod; previousPeriod?: TPeriod } => {
    const termTypePeriods = periods.filter((p) => p.periodTypeId == periodTypeId).sort((a, b) => a.id - b.id);
    const activePeriodIndex = termTypePeriods.findIndex((p) => p.isActive == 1);

    return {
        activePeriod: termTypePeriods[activePeriodIndex],
        previousPeriod: termTypePeriods[activePeriodIndex - 1],
    };
};

/**
 * Knows if a term is renewable
 * The term can be renewed if it has expired during the previous period of its own period type.
 * At the moment we cannot renew a contract that has expired on a period prior to the previously closed period.
 * Mirrors the backend `PUT /term/:id/renew` checks, which only look at the term's period type
 * (not the period selected in the front-end, which may belong to another type in multi-period).
 * @param periods periods array
 * @param term current term data
 * @returns true or false
 */
export const isTermRenewable = (periods: TPeriod[], term: ITerm) => {
    const { hasEnded, periodTypeId, endDate, creationDate } = term;

    if (!hasEnded || !endDate) return false;

    const { activePeriod, previousPeriod } = getRenewalPeriods(periods, periodTypeId);

    if (!activePeriod || !previousPeriod) return false;

    // Backend requires a term_period_data row on the previous period: the term expired in it,
    // or was created in it with an already past end date (history import) and ended at its close
    const termEndDay = toUtcDay(endDate);
    const previousStartDay = toUtcDay(previousPeriod.periodStart);
    const hadPreviousPeriodData = termEndDay >= previousStartDay || toUtcDay(creationDate) >= previousStartDay;

    return hadPreviousPeriodData && termEndDay < toUtcDay(activePeriod.periodStart);
};
